import { faCamera, faLocationCrosshairs, faTriangleExclamation } from "@fortawesome/pro-duotone-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, Button, Typography } from "@mui/material"
import { useTranslation } from "react-i18next"
import styles from "./GrantAccessModal.module.scss"

type GrantAccessModalProps = {
    handleAccess: any
    isLoading: boolean
    /**
     * Permission is denied at browser level, so re-requesting is a silent
     * no-op. A page cannot open the browser's permission UI, and every browser
     * puts it somewhere different — so the copy stays generic and points at
     * "site settings" rather than naming an icon that may not exist.
     */
    blocked?: boolean
    /** Why the last attempt failed. Shown under the grant prompt. */
    error?: string
    /** The only action offered while blocked — a changed permission needs it. */
    onReload?: () => void
}

const GrantAccessModal = (props: GrantAccessModalProps) => {
    const { t: translate } = useTranslation()

    if (props.blocked) {
        return <Box className={styles.grant_access_container}>
            <Box className={styles.grant_access_container__blocked}>
                <FontAwesomeIcon
                    icon={faTriangleExclamation}
                    style={{ "--fa-primary-color": "#e08700", "--fa-secondary-color": "#f3c675", height: "30px", width: "30px" } as any}
                />
                <Typography variant="h6">{translate("locationBlockedHeading")}</Typography>
                <Typography>{translate("locationBlockedText")}</Typography>
            </Box>
            {/* No retry here on purpose: while the browser is denying, asking
                again never prompts, so the only action that can change anything
                is reloading after the setting has been changed. */}
            <Box className={styles.grant_access_container__button_container}>
                <Button
                    className={styles.grant_access_container__primary}
                    onClick={() => props.onReload?.()}
                >
                    {translate("reloadPage")}
                </Button>
            </Box>
        </Box>
    }

    return <Box className={styles.grant_access_container}>
        <Typography variant="h1" className={styles.grant_access_container__heading}>
            {translate("grantAccessHeading")}
        </Typography>
        <Box className={styles.grant_access_container__section}>
            <FontAwesomeIcon icon={faLocationCrosshairs} style={{ "--fa-primary-color": "#575f6e", "--fa-secondary-color": "#575f6e", height: "26px", width: "26px" }} />
            <Box className={styles.grant_access_container__section__details}>
                <Typography variant="h6">
                    {translate("locationAccessHeading")}
                </Typography>
                <Typography>
                    {translate("locationAccessDescription")}
                </Typography>
            </Box>
        </Box>
        <Box className={styles.grant_access_container__section}>
            <FontAwesomeIcon icon={faCamera} style={{ "--fa-primary-color": "#575f6e", "--fa-secondary-color": "#575f6e", height: "26px", width: "26px" }} />
            <Box className={styles.grant_access_container__section__details}>
                <Typography variant="h6">
                    {translate("cameraAccessHeading")}
                </Typography>
                <Typography>
                    {translate("cameraAccessDescription")}
                </Typography>
            </Box>
        </Box>
        <Box className={styles.grant_access_container__button_container}>
            <Button
                className={styles.grant_access_container__primary}
                onClick={() => props.handleAccess()}
                loading={props.isLoading}
            >
                {translate("grantAccessButton")}
            </Button>
        </Box>
        {props.error && (
            <Typography className={styles.grant_access_container__error}>{props.error}</Typography>
        )}
    </Box>
}
export default GrantAccessModal